import { useEffect, useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
    Button,
    Grid,
    useTheme,
    IconButton,
    Modal,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TextField,
} from "@mui/material";

import BaseAdmin from "@/admin/BaseAdmin";
import Stats from "@/admin/Stats";
import SyncButton from "@/admin/SyncButton";
import Webhooks from "@/admin/Webhooks";
import { request } from "@/Api";
import useEntitlements from "@/auth/useEntitlements";
import BaseOrganizationComponents from "@/common/BaseOrganizationComponents";
import ConfirmationDialog from "@/common/ConfirmationDialog";
import FilesUploader from "@/common/FilesUploader";
import { CustomBox } from "@/common/StyledComponents";
import { Heading } from "@/common/typography";
import { APP_NAME } from "@/config";
import {
    FEATURE_RESOURCING_STRATEGY,
    FEATURE_ACCOUNTING_STRATEGY,
} from "@/constants";

export default function Admin() {
    const [activeFinancialTerm, setActiveFinancialTerm] = useState(null);
    const [financialTerms, setFinancialTerms] = useState([]);
    const [financialTermPopupOpen, setFinancialTermPopupOpen] = useState(false);

    const [activeBidDisclaimer, setActiveBidDisclaimer] = useState(null);
    const [bidDisclaimers, setBidDisclaimers] = useState([]);
    const [bidDisclaimerPopupOpen, setBidDisclaimerPopupOpen] = useState(false);

    const [confirmDeleteDisclaimerOpen, setConfirmDeleteDisclaimerOpen] =
        useState(false);

    const [activeSystemContext, setActiveSystemContext] = useState(null);
    const [systemContexts, setSystemContexts] = useState([]);
    const [systemContextPopupOpen, setSystemContextPopupOpen] = useState(false);

    const { hasFeature } = useEntitlements();
    const hasAccountingStrategy = hasFeature(FEATURE_ACCOUNTING_STRATEGY);
    const hasResourcingStrategy = hasFeature(FEATURE_RESOURCING_STRATEGY);

    const tabs = [
        "Stats",
        "Financial Terms",
        "Bid Disclaimers",
        "LLM Workflows",
        "Webhooks",
    ];
    if (hasAccountingStrategy) {
        tabs.push("Accounting");
    }
    if (hasResourcingStrategy) {
        tabs.push("Resourcing");
    }

    const getFinancialTerms = () => {
        request.get("financial-terms").then((response) => {
            setFinancialTerms(response.data);
        });
    };

    const getBidDisclaimers = () => {
        request.get("bid-disclaimers").then((response) => {
            setBidDisclaimers(response.data);
        });
    };

    const getSystemContexts = () => {
        request.get("system-contexts").then((response) => {
            setSystemContexts(response.data);
        });
    };

    const handleOpenPopup = () => {
        setFinancialTermPopupOpen(true);
    };

    const handleClosePopup = () => {
        setFinancialTermPopupOpen(false);
        setActiveFinancialTerm(null);
    };

    const handleEditFinancialTerm = (term) => () => {
        setActiveFinancialTerm(term);
        setFinancialTermPopupOpen(true);
    };

    const handleSwitchChange = (term) => () => {
        request
            .put(`/financial-terms/${term.id}`, {
                ...term,
                public: !term.public,
            })
            .then((response) => {
                setFinancialTerms(
                    financialTerms.map((term) =>
                        term.id == response.data.id ? response.data : term,
                    ),
                );
            });
    };

    const handleEditBidDisclaimer = (disclaimer) => {
        setActiveBidDisclaimer(disclaimer);
        setBidDisclaimerPopupOpen(true);
    };

    const handleDeleteBidDisclaimer = (disclaimer) => {
        setActiveBidDisclaimer(disclaimer);
        setConfirmDeleteDisclaimerOpen(true);
    };

    const deleteBidDisclaimer = () => {
        request
            .delete(`/bid-disclaimers/${activeBidDisclaimer.id}`)
            .then(() => {
                setBidDisclaimers(
                    bidDisclaimers.filter(
                        (d) => d.id !== activeBidDisclaimer.id,
                    ),
                );
            });
    };

    const handleEditSystemContext = (context) => {
        setActiveSystemContext(context);
        setSystemContextPopupOpen(true);
    };

    useEffect(() => {
        getFinancialTerms();
        getBidDisclaimers();
        getSystemContexts();
    }, []);

    return (
        <BaseAdmin title="Admin" tabs={tabs}>
            {/* Stats */}
            <Stats />

            {/* Financial Terms */}
            <Grid
                container
                direction="column"
                justifyContent="flex-start"
                alignItems="stretch"
                spacing={2}
            >
                <Grid item alignSelf="flex-end">
                    <Button onClick={handleOpenPopup}>Add</Button>
                </Grid>

                <Grid item>
                    <Table>
                        <TableBody>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Term Description</TableCell>
                                <TableCell>Billing Schedule</TableCell>
                                <BaseOrganizationComponents>
                                    <TableCell>Public</TableCell>
                                </BaseOrganizationComponents>
                                <TableCell></TableCell>
                            </TableRow>
                            {financialTerms.map((term, index) => (
                                <TableRow key={index}>
                                    <TableCell>{term.name}</TableCell>
                                    <TableCell>{term.description}</TableCell>
                                    <TableCell>
                                        {term.billing_schedule}
                                    </TableCell>
                                    <BaseOrganizationComponents>
                                        <TableCell>
                                            <Switch
                                                checked={term.public}
                                                onChange={handleSwitchChange(
                                                    term,
                                                )}
                                            />
                                        </TableCell>
                                    </BaseOrganizationComponents>
                                    <TableCell width={50}>
                                        <IconButton
                                            onClick={handleEditFinancialTerm(
                                                term,
                                            )}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Grid>

                <FinancialTermPopup
                    open={financialTermPopupOpen}
                    closePopup={handleClosePopup}
                    financialTerms={financialTerms}
                    setFinancialTerms={setFinancialTerms}
                    activeFinancialTerm={activeFinancialTerm}
                />
            </Grid>

            {/* Bid Disclaimers */}
            <Grid
                container
                direction="column"
                justifyContent="flex-start"
                alignItems="stretch"
                spacing={2}
            >
                <Grid item alignSelf="flex-end">
                    <Button
                        variant="contained"
                        onClick={() => setBidDisclaimerPopupOpen(true)}
                    >
                        Add
                    </Button>
                </Grid>

                <Grid item flexGrow={1}>
                    <Table style={{ width: "100%" }} width="100%">
                        <TableBody width="100%">
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Sort Order</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                            {bidDisclaimers.map((disclaimer, index) => (
                                <TableRow key={index}>
                                    <TableCell>{disclaimer.name}</TableCell>
                                    <TableCell>
                                        {disclaimer.description}
                                    </TableCell>
                                    <TableCell>
                                        {disclaimer.sort_order}
                                    </TableCell>
                                    <TableCell width={100}>
                                        <IconButton
                                            onClick={() =>
                                                handleEditBidDisclaimer(
                                                    disclaimer,
                                                )
                                            }
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={() =>
                                                handleDeleteBidDisclaimer(
                                                    disclaimer,
                                                )
                                            }
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Grid>

                <BidDisclaimerPopup
                    open={bidDisclaimerPopupOpen}
                    closePopup={() => {
                        setBidDisclaimerPopupOpen(false);
                        setActiveBidDisclaimer(null);
                    }}
                    bidDisclaimers={bidDisclaimers}
                    setBidDisclaimers={setBidDisclaimers}
                    activeBidDisclaimer={activeBidDisclaimer}
                />
                <ConfirmationDialog
                    open={confirmDeleteDisclaimerOpen}
                    closeDialog={() => setConfirmDeleteDisclaimerOpen(false)}
                    title="Are you sure?"
                    callback={() => {
                        if (activeBidDisclaimer) {
                            deleteBidDisclaimer(activeBidDisclaimer);
                        }
                    }}
                >
                    {`This will delete the bid disclaimer "${activeBidDisclaimer ? activeBidDisclaimer.name : ""}" .`}
                </ConfirmationDialog>
            </Grid>

            {/* LLM Workflows */}
            <Grid
                container
                direction="column"
                justifyContent="flex-start"
                alignItems="stretch"
                spacing={2}
            >
                <Grid item flexGrow={1}>
                    <Table style={{ width: "100%" }} width="100%">
                        <TableBody width="100%">
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                            {systemContexts.map((context, index) => (
                                <TableRow key={index}>
                                    <TableCell>{context.name}</TableCell>
                                    <TableCell>{context.description}</TableCell>
                                    <TableCell width={50}>
                                        <IconButton
                                            onClick={() =>
                                                handleEditSystemContext(context)
                                            }
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Grid>

                <SystemContextPopup
                    open={systemContextPopupOpen}
                    closePopup={() => {
                        setSystemContextPopupOpen(false);
                        setActiveSystemContext(null);
                    }}
                    systemContexts={systemContexts}
                    setSystemContexts={setSystemContexts}
                    activeSystemContext={activeSystemContext}
                />
            </Grid>

            {/* Webhooks */}
            <Webhooks />

            {/* Accounting */}
            {hasAccountingStrategy && (
                <Grid container spacing={3} paddingBottom={5}>
                    <SyncButton
                        name="Sync QB Invoices"
                        route="/invoices/refersh-invoices-test"
                        notes="Update Invoice table, add if missing, archive if no longer in QB response"
                    />
                    <SyncButton
                        name="Sync QB Income Summary"
                        route="/admin/refresh-customer-income-test"
                        notes="Refresh project accounting numbers from QB (from invoice total)"
                    />
                    <SyncButton
                        name="Sync QB Income Summary (TMP)"
                        route="/admin/refresh-customer-income-tmp-test"
                        notes="Refresh project accounting numbers from QB (from qb report)"
                    />
                    <SyncButton
                        name="Sync Accounting Bills"
                        route="/accounting-bills/refersh-accounting-bills"
                        notes="Update Accounting Bills table by inserting any new QB bills"
                    />
                    <SyncButton
                        name="Sync Accounting Bill Attachments"
                        route="/accounting-bill-attachments/refersh"
                        notes="Add Attachables if they are being newly created"
                    />
                </Grid>
            )}

            {/* Resourcing */}
            {hasResourcingStrategy && (
                <Grid container spacing={3} paddingBottom={5}>
                    <SyncButton
                        name="Sync Projects Forecast Hours"
                        route="/admin/refresh-projects-forecast-hours"
                        notes="Update project hours count from Forecast (should be removed soon)"
                    />
                    <SyncButton
                        name="Sync Project Float Actual"
                        route="/admin/refresh-all-project-float-actual"
                        notes="Update all project Internal Actuals from Float"
                    />
                    <SyncButton
                        name="Sync All Float Projects"
                        route="/admin/refresh-all-float-projects"
                        notes={`Correct Float projects, treating ${APP_NAME} as source of truth`}
                    />
                    <SyncButton
                        name="Sync Float IDs"
                        route="/admin/refresh-float-ids"
                        notes={`Update float ids in ${APP_NAME}, will become less useful as data are initially migrated to float`}
                    />
                </Grid>
            )}
        </BaseAdmin>
    );
}

const FinancialTermPopup = ({
    open,
    closePopup,
    setFinancialTerms,
    financialTerms,
    activeFinancialTerm,
}) => {
    const theme = useTheme();
    const [financialTerm, setFinancialTerm] = useState({});
    const handleInputChange = (field) => (event) => {
        setFinancialTerm({ ...financialTerm, [field]: event.target.value });
    };
    // const setProjectTypeId = (value) => {
    //     setFinancialTerm({...financialTerm, "project_type_id": value})
    // }
    const addFinancialTerm = () => {
        request
            .post(`/financial-terms`, {
                is_custom: false,
                ...financialTerm,
            })
            .then((response) => {
                closePopup();
                setFinancialTerms([...financialTerms, response.data]);
            });
    };
    const updateFinancialTerm = () => {
        request
            .put(`/financial-terms/${activeFinancialTerm.id}`, {
                ...financialTerm,
            })
            .then((response) => {
                closePopup();
                setFinancialTerms(
                    financialTerms.map((term) =>
                        term.id == response.data.id ? response.data : term,
                    ),
                );
            });
    };
    useEffect(() => {
        setFinancialTerm(activeFinancialTerm || {});
    }, [open, activeFinancialTerm]);
    return (
        <Modal open={open} onClose={closePopup}>
            <CustomBox
                style={{
                    background: theme.palette.background.paper,
                    overflow: "auto",
                }}
            >
                <Heading>{`${activeFinancialTerm ? "Update" : "Add"} Financial Term`}</Heading>
                <h3>Name</h3>
                <TextField
                    fullWidth
                    onChange={handleInputChange("name")}
                    value={financialTerm.name || ""}
                    size="small"
                />
                <h3>Term description</h3>
                <TextField
                    fullWidth
                    multiline
                    rows={5}
                    onChange={handleInputChange("description")}
                    value={financialTerm.description || ""}
                    size="small"
                />
                <h3 style={{ marginBottom: 0 }}>Billing schedule</h3>
                <span style={{ fontSize: 12, lineHeight: 2.5, color: "grey" }}>
                    example: 60/30/10
                </span>
                <br />
                <TextField
                    sx={{ paddingBottom: 3 }}
                    fullWidth
                    onChange={handleInputChange("billing_schedule")}
                    value={financialTerm.billing_schedule || ""}
                    size="small"
                />
                {/* <h3>Project Type</h3>
                <ApiAutocomplete
                    size={"small"}
                    sx={{width: "100%", paddingBottom: 3}}
                    value={financialTerm.project_type_id}
                    apiRoute={"project-types"}
                    fields={["name"]}
                    onChange={(e, v)=>{setProjectTypeId(v)}}
                /> */}
                <Button
                    variant="contained"
                    onClick={
                        activeFinancialTerm
                            ? updateFinancialTerm
                            : addFinancialTerm
                    }
                >
                    {`${activeFinancialTerm ? "Update" : "Create"}`}
                </Button>
            </CustomBox>
        </Modal>
    );
};

const BidDisclaimerPopup = ({
    open,
    closePopup,
    setBidDisclaimers,
    bidDisclaimers,
    activeBidDisclaimer,
}) => {
    const theme = useTheme();
    const defaultBidDisclaimer = {
        name: "",
        description: "",
        sort_order: 0,
    };
    const [bidDisclaimer, setBidDisclaimer] = useState({
        ...defaultBidDisclaimer,
    });

    const handleInputChange = (field) => (event) => {
        setBidDisclaimer({ ...bidDisclaimer, [field]: event.target.value });
    };

    const addBidDisclaimer = () => {
        request
            .post(`/bid-disclaimers`, { ...bidDisclaimer })
            .then((response) => {
                closePopup();
                setBidDisclaimer({ ...defaultBidDisclaimer });
                setBidDisclaimers([...bidDisclaimers, response.data]);
            });
    };

    const updateBidDisclaimer = () => {
        request
            .put(`/bid-disclaimers/${activeBidDisclaimer.id}`, {
                ...bidDisclaimer,
            })
            .then((response) => {
                closePopup();
                setBidDisclaimer({ ...defaultBidDisclaimer });
                setBidDisclaimers(
                    bidDisclaimers.map((d) =>
                        d.id === response.data.id ? response.data : d,
                    ),
                );
            });
    };

    useEffect(() => {
        setBidDisclaimer(activeBidDisclaimer || { ...defaultBidDisclaimer });
    }, [open, activeBidDisclaimer]);

    return (
        <Modal open={open} onClose={closePopup}>
            <CustomBox
                style={{
                    background: theme.palette.background.paper,
                    overflow: "auto",
                }}
            >
                <Heading>{`${activeBidDisclaimer ? "Update" : "Add"} Bid Disclaimer`}</Heading>
                <h3>Name</h3>
                <TextField
                    fullWidth
                    onChange={handleInputChange("name")}
                    value={bidDisclaimer.name || ""}
                    size="small"
                />
                <h3>Description</h3>
                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    onChange={handleInputChange("description")}
                    value={bidDisclaimer.description || ""}
                    size="small"
                />
                <h3>Sort Order</h3>
                <TextField
                    fullWidth
                    type="number"
                    onChange={handleInputChange("sort_order")}
                    value={bidDisclaimer.sort_order || ""}
                    size="small"
                />
                <Grid
                    container
                    justifyContent="flex-end"
                    alignItems="center"
                    spacing={2}
                    sx={{ marginTop: 2 }}
                >
                    <Grid item>
                        <Button
                            variant="contained"
                            onClick={
                                activeBidDisclaimer
                                    ? updateBidDisclaimer
                                    : addBidDisclaimer
                            }
                            sx={{ marginTop: 2 }}
                        >
                            {activeBidDisclaimer ? "Update" : "Create"}
                        </Button>
                    </Grid>
                </Grid>
            </CustomBox>
        </Modal>
    );
};

const SystemContextPopup = ({
    open,
    closePopup,
    setSystemContexts,
    systemContexts,
    activeSystemContext,
}) => {
    const theme = useTheme();
    const [systemContext, setSystemContext] = useState(null);

    const handleInputChange = (field) => (event) => {
        setSystemContext({ ...systemContext, [field]: event.target.value });
    };

    const updateSystemContext = () => {
        request
            .patch(`/system-contexts/${activeSystemContext.name_id}`, {
                ...systemContext,
            })
            .then((response) => {
                closePopup();
                setSystemContext(null);
                setSystemContexts(
                    systemContexts.map((c) =>
                        c.name_id === response.data.name_id ? response.data : c,
                    ),
                );
            });
    };

    useEffect(() => {
        setSystemContext(activeSystemContext);
    }, [open, activeSystemContext]);

    if (!systemContext) {
        return null;
    }

    return (
        <Modal open={open} onClose={closePopup}>
            <CustomBox
                style={{
                    background: theme.palette.background.paper,
                    overflow: "auto",
                    minWidth: 600,
                }}
            >
                <Heading>Update System Context</Heading>
                <h3>Name</h3>
                <TextField
                    disabled
                    fullWidth
                    value={systemContext.name_id || ""}
                    size="small"
                />
                <h3>Display Name</h3>
                <TextField
                    fullWidth
                    onChange={handleInputChange("name")}
                    value={systemContext.name || ""}
                    size="small"
                />
                <h3>Description</h3>
                <TextField
                    fullWidth
                    multiline
                    rows={2}
                    onChange={handleInputChange("description")}
                    value={systemContext.description || ""}
                    size="small"
                />
                <h3>System Prompt Content</h3>
                <TextField
                    fullWidth
                    multiline
                    rows={12}
                    onChange={handleInputChange("content")}
                    value={systemContext.content || ""}
                    size="small"
                    inputProps={{
                        style: { fontFamily: "monospace", fontSize: 13 },
                    }}
                />

                {activeSystemContext && activeSystemContext.id && (
                    <div style={{ marginTop: 24, marginBottom: 8 }}>
                        <FilesUploader
                            key={activeSystemContext.id}
                            acceptedFormats={[
                                ".pdf",
                                ".docx",
                                ".csv",
                                ".xlsx",
                                ".xlsm",
                                ".xls",
                            ]}
                            files={activeSystemContext.files || []}
                            modelType="llm_system_context"
                            modelId={activeSystemContext.id}
                            title="Attached Knowledge Files"
                            single={false}
                            disabled={false}
                        />
                    </div>
                )}

                <Grid
                    container
                    justifyContent="flex-end"
                    alignItems="center"
                    spacing={2}
                    sx={{ marginTop: 2 }}
                >
                    <Grid item>
                        <Button
                            variant="contained"
                            onClick={updateSystemContext}
                            sx={{ marginTop: 2 }}
                            disabled={!activeSystemContext}
                        >
                            Update
                        </Button>
                    </Grid>
                </Grid>
            </CustomBox>
        </Modal>
    );
};
